<script setup lang="ts">
import type { IndexPageProperties } from '@optionway/models';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.pages?.index;
const homeTitleConfig = $appConfig.app?.components?.homeTitle;

const pageConfig = computed<IndexPageProperties>(() => {
  return {
    components: config?.components,
    hideComponents: config?.hideComponents,
  };
});

const showSearchForm = computed(() => !homeTitleConfig?.hideComponents?.searchForm);

const isStickyViewport = useMediaQuery('(min-width: 1300px) and (min-height: 800px)');

const stuckSentinel = useTemplateRef<HTMLElement>('stuckSentinel');
const searchCriteriaStore = useSearchCriteriaStore();

const isStuck = ref(false);

const searchFormAutoFocus = ref(false);

const onChangeOrigin = async () => {
  await nextTick();
  searchFormAutoFocus.value = true;
};

useIntersectionObserver(stuckSentinel, (entries: IntersectionObserverEntry[]) => {
  const entry = entries[0];
  if (!entry) return;
  const stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
  isStuck.value = stuck && isStickyViewport.value && searchCriteriaStore.searchRequest.tripType !== 'MULTI_DEST';
});

watch(isStickyViewport, (active) => {
  if (!active) isStuck.value = false;
});

definePageMeta({
  title: 'pages.index.seo.title',
  description: 'pages.index.seo.description',
  middleware: ['entry-point'],
});
</script>

<template>
  <div>
    <HomeHero />

    <template v-if="showSearchForm">
      <div
        ref="stuckSentinel"
        class="-mt-32 h-0 md:-mt-36 lg:-mt-32"
        aria-hidden="true"
      />
      <div
        :class="[
          'transition-all duration-300 ease-out',
          isStickyViewport && !searchCriteriaStore.isMultiDestination && 'sticky top-0 z-20',
          isStuck ? 'bg-background shadow-md' : 'bg-transparent shadow-none',
        ]"
      >
        <div :class="isStuck ? '' : 'px-4 md:px-6 lg:px-8'">
          <UiCustomContainer>
            <!-- relative carries the overlap: painted after the positioned hero, so the background covers the image. -->
            <div
              :class="[
                'relative mx-auto w-full transition-all duration-300 ease-out',
                isStuck
                  ? 'rounded-none bg-transparent py-3 shadow-none md:py-4'
                  : 'max-w-6xl rounded-3xl bg-card p-4 shadow-2xl md:p-6',
              ]"
            >
              <SearchForm
                v-model:auto-focus="searchFormAutoFocus"
                :compact="isStuck"
              />
            </div>
          </UiCustomContainer>
        </div>
      </div>
    </template>

    <div
      :class="['relative p-4 transition-all duration-300 ease-out md:p-6 lg:p-8', isStuck ? 'lg:pt-28' : 'lg:pt-14']"
    >
      <UiCustomContainer>
        <HomeLowestFares
          v-if="!pageConfig.hideComponents?.homeLowestFares"
          @change-origin="onChangeOrigin"
        />

        <hr class="mx-5 my-5 sm:mx-10 sm:my-10" />

        <HomeOptionwayChart v-if="!pageConfig.hideComponents?.homeOptionwayChart" />
      </UiCustomContainer>
      <hr class="mx-5 my-5 sm:mx-10 sm:my-10" />
      <HomeOptionwayAirlines v-if="!pageConfig.hideComponents?.homeOptionwayAirlines" />
    </div>

    <HomeNewsletterSignup v-if="!pageConfig.hideComponents?.homeNewsletterSignup" />
  </div>
</template>
